import { useEffect, useId, useRef, useState, type KeyboardEvent } from "react"
import cx from "clsx"
import ChevronIcon from "../assets/icon-chevron-down.svg?react"
import { TickMark } from "./CheckboxMarks"
import { describedBy } from "./describedBy"
import { FieldHint } from "./FieldHint"
import styles from "./SelectField.module.css"

type Option = { value: string; label: string }

/** How long a pause in typing ends one search, as in the APG's example. */
const TYPING_PAUSE_MS = 500

type Props = {
  id: string
  label: string
  value: string
  onChange: (value: string) => void
  options: ReadonlyArray<Option>
  /**
   * Shown until a choice is made. On an optional field it is offered again to
   * undo a choice; on a required one it is not, so the only way forward is a
   * real option.
   */
  placeholder?: string
  size?: "small" | "large"
  disabled?: boolean
  error?: string
  /** How to answer it, shown under the label and read with the control. */
  hint?: string
  // Error styling without a message of its own — for a field that is one part of
  // a group, where the message belongs to the group rather than to each part.
  invalid?: boolean
  required?: boolean
  className?: string
}

/**
 * Picks one option from a short list — a select-only combo box (WAI-ARIA APG):
 * a button that shows the choice and opens a list of the options under it.
 * The design system's "Drop-Down Icon", in its large and small sizes.
 */
export function SelectField({
  id,
  label,
  value,
  onChange,
  options,
  placeholder,
  size = "large",
  disabled,
  error,
  hint,
  invalid,
  required,
  className,
}: Props) {
  const listboxId = useId()
  const [open, setOpen] = useState(false)
  const [activeIndex, setActiveIndex] = useState(0)
  const controlRef = useRef<HTMLDivElement>(null)
  const listboxRef = useRef<HTMLUListElement>(null)
  const typed = useRef("")
  const typingPause = useRef<ReturnType<typeof setTimeout>>(undefined)

  useEffect(() => {
    if (!open) return
    function closeIfOutside(event: Event) {
      if (!controlRef.current?.contains(event.target as Node)) setOpen(false)
    }
    document.addEventListener("pointerdown", closeIfOutside)
    document.addEventListener("focusin", closeIfOutside)
    return () => {
      document.removeEventListener("pointerdown", closeIfOutside)
      document.removeEventListener("focusin", closeIfOutside)
    }
  }, [open])

  // Arrowing past the visible rows brings the active one into view.
  useEffect(() => {
    // Optional call: jsdom has no scrollIntoView, and a missing scroll is not a
    // reason for the control to throw.
    listboxRef.current
      ?.querySelector("[data-active]")
      ?.scrollIntoView?.({ block: "nearest" })
  }, [activeIndex, open])

  const chosen = options.find((option) => option.value === value)
  const offered =
    placeholder !== undefined && !required
      ? [{ value: "", label: placeholder }, ...options]
      : options

  const optionId = (index: number) => `${listboxId}-${index}`

  /** Opens the list at the option chosen, or at its first. */
  function openList() {
    setActiveIndex(
      Math.max(
        0,
        offered.findIndex((option) => option.value === value),
      ),
    )
    setOpen(true)
  }

  function onKeyDown(event: KeyboardEvent<HTMLButtonElement>) {
    const last = offered.length - 1

    if (event.key === "ArrowDown" || event.key === "ArrowUp") {
      event.preventDefault()
      if (!open) {
        openList()
        return
      }
      setActiveIndex((i) => {
        if (event.key === "ArrowDown") return i >= last ? 0 : i + 1
        return i <= 0 ? last : i - 1
      })
      return
    }

    if (event.key === "Home" || event.key === "End") {
      event.preventDefault()
      setActiveIndex(event.key === "Home" ? 0 : last)
      setOpen(true)
      return
    }

    // Escape closes the list and goes no further, or a dialog the field is in
    // would close with it.
    if (event.key === "Escape" && open) {
      event.preventDefault()
      event.stopPropagation()
      setOpen(false)
      return
    }

    if ((event.key === "Enter" || event.key === " ") && open) {
      event.preventDefault()
      choose(offered[activeIndex])
      return
    }

    const shortcut = event.ctrlKey || event.metaKey || event.altKey
    if (event.key.length === 1 && !shortcut) typeAhead(event.key)
  }

  /**
   * Moves to the option that what has been typed starts. One letter typed
   * again steps on to its next option; letters typed together are the start
   * of one, which the active option may already be. A pause starts afresh.
   */
  function typeAhead(key: string) {
    typed.current += key.toLowerCase()
    clearTimeout(typingPause.current)
    typingPause.current = setTimeout(() => {
      typed.current = ""
    }, TYPING_PAUSE_MS)

    const repeated = [...typed.current].every((c) => c === typed.current[0])
    const wanted = repeated ? typed.current[0] : typed.current
    const from = open ? activeIndex + (repeated ? 1 : 0) : 0
    const match = offered
      .map((_, step) => (from + step) % offered.length)
      .find((i) => offered[i].label.toLowerCase().startsWith(wanted))
    if (match === undefined) return
    setActiveIndex(match)
    setOpen(true)
  }

  function choose(option: Option) {
    onChange(option.value)
    setOpen(false)
  }

  return (
    <div
      className={cx(
        styles.field,
        styles[size],
        { [styles.error]: error || invalid },
        className,
      )}
    >
      <label className={styles.label} htmlFor={id}>
        {label}
        {required && (
          <span aria-hidden="true" className={styles.required}>
            {" "}
            *
          </span>
        )}
      </label>
      {hint && <FieldHint id={`${id}-hint`}>{hint}</FieldHint>}
      <div className={styles.control} ref={controlRef}>
        <button
          type="button"
          id={id}
          role="combobox"
          // Safari leaves a button out of Tab order unless it has a tabindex.
          tabIndex={0}
          className={styles.trigger}
          aria-haspopup="listbox"
          aria-expanded={open}
          aria-controls={open ? listboxId : undefined}
          aria-activedescendant={open ? optionId(activeIndex) : undefined}
          aria-invalid={!!error || !!invalid}
          aria-required={required || undefined}
          aria-describedby={describedBy(
            hint && `${id}-hint`,
            error && `${id}-error`,
          )}
          disabled={disabled}
          onClick={() => (open ? setOpen(false) : openList())}
          onKeyDown={onKeyDown}
        >
          <span className={cx(styles.value, { [styles.placeholder]: !chosen })}>
            {chosen?.label ?? placeholder}
          </span>
          <span className={styles.chevronBox} aria-hidden="true">
            <ChevronIcon className={styles.chevron} />
          </span>
        </button>
        {open && (
          <ul
            ref={listboxRef}
            role="listbox"
            id={listboxId}
            aria-label={label}
            className={styles.listbox}
            onMouseDown={(event) => event.preventDefault()}
          >
            {offered.map((option, index) => (
              <li
                key={option.value}
                id={optionId(index)}
                role="option"
                aria-selected={option.value === value}
                data-active={index === activeIndex ? "" : undefined}
                className={cx(styles.option, {
                  [styles.placeholderOption]: !options.includes(option),
                })}
                onClick={() => choose(option)}
                onMouseEnter={() => setActiveIndex(index)}
              >
                {option.label}
                {option === chosen && (
                  <span className={styles.tick} aria-hidden="true">
                    <TickMark />
                  </span>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>
      {error && (
        <span
          id={`${id}-error`}
          className={styles.errorMessage}
          aria-live="polite"
        >
          {error}
        </span>
      )}
    </div>
  )
}
